import React, { useCallback, useEffect, useMemo } from 'react';

import { ReportToolTip, StudentMetricsDisplay } from 'pages/reports/components/StudentMetricsDisplay/StudentMetricsDisplay';
import type { AverageMetrics } from 'pages/reports/components/StudentMetricsDisplay/StudentMetricsDisplay';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { ViewOptionsBar } from 'components/ViewOptionsBar/ViewOptionsBar';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { orderBy, SortDescriptor } from 'utils/dataQuery';
import { buildExportFileName, GenerateUUID } from 'utils';
import { Card, Col, Row, Table } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { EResourceType } from 'pages/reports/types';
import { useNotification } from 'app/notifications';
import { ReportSection } from 'apis/ReportApi';
import { useSuperState } from 'hooks';
import { CSVLink } from 'react-csv';

import 'pages/reports/components/ChartInfoBox/styles/chart-info-box.scss';
import 'pages/reports/components/Report67/Report67.scss';

import {
    avg,
    calculateHourMinFormat,
    EXPANDED_COLUMN_WIDTH,
    formatDate as formatDate,
    getStatus,
    getUniqueStudentNames,
    isDateWithinLast7Days,
    normalizeScore,
    renderScore,
    renderStatus,
    ResourceTypeLabelsList,
    sortableHeader,
    useResourceTypeLabelsList
} from '../ReportFunctions';
import { createSortChangeHandler, GridSortChangeEvent } from '../sortUtils';

interface Report67Props {
    reportSections: ReportSection[];
}

type MetricType = 'Status' | 'Score' | 'Time Spent' | 'Attempts';

const STUDENT_DETAIL_REPORT_ID = '68';
const MAX_DISPLAY_ROWS = 500;

// Utility Functions
const formatDisplayDate = (date: string | null | undefined): string => {

    if (!date) return '---';
    return new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(date));

};

const navigateToStudentDetail = (studentProfileId: number) => {

    const url = new URL(window.location.href);
    url.pathname = `/reports/${STUDENT_DETAIL_REPORT_ID}`;
    url.searchParams.set('StudentsCSV', studentProfileId.toString());
    window.location.href = url.toString();

};

const getMetricValue = (resource: any, metric: MetricType): string | number | React.ReactElement => {

    switch (metric) {

        case 'Time Spent':
            return resource.TimeSpentInMinutes ? calculateHourMinFormat(resource.TimeSpentInMinutes) : '---';
        case 'Attempts':
            return resource.Attempts ? `${resource.Attempts}x` : '---';
        case 'Status':
            return renderStatus(getStatus(resource));
        case 'Score':

            // Only show score for Assessment resources (ResourceTypeID === 0)
            if (resource.ResourceTypeID === 0) {

                return (resource.Attempts === 0 || !resource.Attempts) ? <FormattedMessage defaultMessage="No Score" id="no_score.defd94f7" /> : renderScore(resource.HighestScore);

            }
            return '---';
        default:
            return '---';

    }

};

const aggregateResourceMetrics = (records: any[]) => {

    const validScores = records.map(r => r.HighestScore).filter(s => s !== null && s !== undefined);
    const highestScore = validScores.length > 0 ? Math.max(...validScores) : null;
    const completedRecord = records.find(r => r.Completed);
    const completed = completedRecord?.Completed || records[0].Completed;

    if (completed && highestScore !== null) {

        completed.Score = highestScore;

    }

    return {
        Attempts: records.reduce((sum, r) => sum + (r.Attempts || 0), 0),
        Completed: completed,
        HighestScore: highestScore,
        TimeSpentInMinutes: records.reduce((sum, r) => sum + (r.TimeSpentInMinutes || 0), 0)
    };

};

export const Report67 = ({
    reportSections
}: Report67Props) => {

    // State Management
    const resourceTypeLabels = useResourceTypeLabelsList();

    const [state, setState] = useSuperState({
        averageMetrics: {
            activePercent: 0,
            completedPercent: 0,
            startedPercent: 0,
            studentProgress: 0,
            timeSpent: 0
        } as AverageMetrics,
        currentMetric: 'Status' as MetricType,
        exportFileName: 'CourseSummaryReport_Student_Status.csv',
        exportRows: [] as any[],
        showResourcesSelected: resourceTypeLabels,
        sort: [] as SortDescriptor[],
        students: [] as any[],
        studentView: true,
        tableKey: 'tableKey',
        tableRows: [] as any[]
    });

    const { averageMetrics, currentMetric, exportFileName, exportRows, showResourcesSelected, sort, students, studentView, tableKey, tableRows } = state;

    const { createToast } = useNotification();
    const { formatMessage } = useIntl();

    // getStatus returns canonical identifiers; use this wherever the status is shown as plain text.
    const statusLabel = (status: string) => status === 'Complete'
        ? formatMessage({ id: 'complete.6757643c', defaultMessage: 'Complete' })
        : status === 'In Progress'
            ? formatMessage({ id: 'in_progress.0e4b5895', defaultMessage: 'In Progress' })
            : formatMessage({ id: 'not_started.6c6f679c', defaultMessage: 'Not Started' });

    const sortedData = useMemo(() => orderBy(tableRows, sort), [tableRows, sort]);
    const displayData = useMemo(() => sortedData.slice(0, MAX_DISPLAY_ROWS), [sortedData]);

    const showStudents = useCallback(() => {

        const mergedData = mergeByStudentProfileId(reportSections[0].Components[0].Results?.Data);
        setState({ tableRows: mergedData });

    }, [reportSections]);

    const showResources = useCallback(() => {

        const transformedData = transformData(reportSections[0].Components[0].Results?.Data, showResourcesSelected);
        setState({ tableRows: transformedData });

    }, [reportSections, showResourcesSelected]);

    // Button configurations
    const categories = useMemo(() => [
        {
            action: () => {

                setState({ studentView: true }); showStudents();

            }, title: formatMessage({ id: 'student.4f46d2e3', defaultMessage: 'Student' })
        },
        {
            action: () => {

                setState({ studentView: false }); showResources();

            }, title: formatMessage({ id: 'resource.6e70fb89', defaultMessage: 'Resource' })
        }
    ], [showStudents, showResources, formatMessage]);

    const metrics = useMemo(() => [
        { action: () => setState({ currentMetric: 'Status' }), title: formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' }) },
        { action: () => setState({ currentMetric: 'Score' }), title: formatMessage({ id: 'score.6ed6415a', defaultMessage: 'Score' }) },
        { action: () => setState({ currentMetric: 'Time Spent' }), title: formatMessage({ id: 'time_spent.da775c0c', defaultMessage: 'Time Spent' }) },
        { action: () => setState({ currentMetric: 'Attempts' }), title: formatMessage({ id: 'attempts.ae7c8eee', defaultMessage: 'Attempts' }) }
    ], [formatMessage]);

    // Single tooltip renderer shared by the student and resource views so both show identical
    // data. Accepts either shape: HighestScore/TimeSpentInMinutes or Score/TimeSpent.
    const renderMetricsTooltip = (dataItem: any) => {

        if (!dataItem) return (
            <p style={{ textAlign: 'start' }}>
                <FormattedMessage defaultMessage="No Data" id="no_data.42f7c5d3" />
            </p>
        );
        const isAssessment = dataItem.ResourceTypeID === 0;
        const score = dataItem.HighestScore ?? dataItem.Score;
        const minutes = dataItem.TimeSpentInMinutes ?? dataItem.TimeSpent ?? 0;
        const scoreDisplay = isAssessment
            ? (dataItem.Attempts === 0 || !dataItem.Attempts)
                ? formatMessage({ id: 'no_score.defd94f7', defaultMessage: 'No Score' })
                : `${normalizeScore(score)}%`
            : '---';

        return (
            <p style={{ textAlign: 'start' }}>
                <FormattedMessage defaultMessage="Status: {status}" id="status_status.7e3c5400" values={{ status: statusLabel(getStatus(dataItem)) }} />
                {' '}
                <br />
                <FormattedMessage defaultMessage="Score: {score}" id="score_score.d049d037" values={{ score: scoreDisplay }} />
                {' '}
                <br />
                <FormattedMessage defaultMessage="Time Spent: {time}" id="time_spent_time.a963f270" values={{ time: calculateHourMinFormat(Math.round(minutes)) }} />
                <br />
                <FormattedMessage defaultMessage="Attempts: {count}x" id="attempts_countx.c7ccf135" values={{ count: dataItem.Attempts ?? 0 }} />
                {' '}
                <br />
                <FormattedMessage defaultMessage="Last Accessed Date:" id="last_accessed_date.5f224487" />
                {' '}
                <br />
                {formatDisplayDate(dataItem.CourseLastAccessDate)}
            </p>
        );

    };

    const studentViewLessonColumns = useMemo(() => {

        if (!tableRows || tableRows.length === 0) return [];

        // Step 1: Collect all unique lessons and resources across all students
        const lessonMap: Record<string, any> = {};
        tableRows.forEach(student => {

            student.Lessons?.forEach((lesson: any) => {

                if (!lessonMap[lesson.LessonName]) {

                    lessonMap[lesson.LessonName] = { ...lesson, resourcesMap: {} };

                }

                lesson.Resources?.forEach((resource: any) => {

                    if (!lessonMap[lesson.LessonName].resourcesMap[resource.ResourceId]) {

                        lessonMap[lesson.LessonName].resourcesMap[resource.ResourceId] = {
                            ResourceDisplayOrder: resource.ResourceDisplayOrder ?? 0,
                            ResourceId: resource.ResourceId,
                            ResourceName: resource.ResourceName,
                            ResourceTypeID: resource.ResourceTypeID
                        };

                    }

                });

            });

        });

        // Step 2: Build flat lesson/resource structure
        const selectedValues = showResourcesSelected.map(selected => selected.value);
        const allKnownValues = ResourceTypeLabelsList.map(r => r.value);
        return Object.values(lessonMap).map((lesson: any) => {

            const sortedResources = Object.values(lesson.resourcesMap)
                .sort((a: any, b: any) => (a.ResourceDisplayOrder ?? 0) - (b.ResourceDisplayOrder ?? 0))
                .filter((resource: any) => selectedValues.includes(resource.ResourceTypeID) || !allKnownValues.includes(resource.ResourceTypeID));
            return {
                lessonName: lesson.LessonName,
                resources: sortedResources as any[]
            };

        });

    }, [tableRows, showResourcesSelected]);

    const getStudentResourceMetric = (student: any, resourceId: number) => {

        const lesson = student.Lessons?.find((l: any) => l.Resources?.some((r: any) => r.ResourceId === resourceId));
        const resource = lesson?.Resources?.find((r: any) => r.ResourceId === resourceId);
        if (!resource) return null;
        switch (currentMetric) {

            case 'Time Spent':
                return resource.TimeSpentInMinutes ?? 0;
            case 'Attempts':
                return resource.Attempts ?? 0;
            case 'Status':
                return getStatus(resource);
            case 'Score':

                // Only return score for Assessment resources
                if (resource.ResourceTypeID === 0) {

                    return normalizeScore(resource.HighestScore ?? 0);

                }
                return null;
            default:
                return null;

        }

    };

    const getResourceStudentMetric = (resourceRow: any, studentId: number) => {

        const studentData = resourceRow.StudentData?.find((s: any) => s.StudentProfileId === studentId);
        if (!studentData) return null;
        switch (currentMetric) {

            case 'Time Spent':
                return studentData.TimeSpent ?? 0;
            case 'Attempts':
                return studentData.Attempts ?? 0;
            case 'Status':
                return getStatus(studentData);
            case 'Score':

                // Only return score for Assessment resources
                if (resourceRow.ResourceTypeID === 0) {

                    return normalizeScore(studentData.Score ?? 0);

                }
                return null;
            default:
                return null;

        }

    };

    const getSortableValue = (item: any, field?: string) => {

        if (!field) return null;
        if (field.startsWith('resource-')) {

            const resourceId = Number(field.replace('resource-', ''));
            return getStudentResourceMetric(item, resourceId);

        }
        if (field.startsWith('student-')) {

            const studentId = Number(field.replace('student-', ''));
            return getResourceStudentMetric(item, studentId);

        }
        if (field === 'CourseLastAccessDate' || field === 'CourseDateCompleted') {

            return item[field] ? new Date(item[field]).getTime() : null;

        }
        if (field === 'AverageScore') return normalizeScore(Number(item[field]));
        const value = item[field];
        return value;

    };

    const handleSortChange = useCallback((event: GridSortChangeEvent) => {

        createSortChangeHandler(event, sort, val => setState({ sort: val }), getSortableValue);

    }, [sort]);

    const mergeByStudentProfileId = (data: any) => {

        const studentMap: any = {};

        // Group all records by student
        data.forEach((item: any) => {

            const studentId = item.StudentProfileId;
            if (!studentMap[studentId]) {

                studentMap[studentId] = { ...item, _records: [], Lessons: [] };

            }
            studentMap[studentId]._records.push({ ...item });

        });

        // Process each student's records
        const mergedStudents = Object.values(studentMap).map((student: any) => {

            const lessonMap: any = {};

            // Group records by lesson
            student._records.forEach((record: any) => {

                const lessonName = record.LessonName;
                if (!lessonMap[lessonName]) {

                    lessonMap[lessonName] = { ...record, _records: [], Resources: [] };

                }
                lessonMap[lessonName]._records.push({ ...record });

            });

            // Build lessons with resources
            const lessons = Object.values(lessonMap)
                .map((lesson: any) => {

                    const resourceMap: any = {};

                    // Group resources
                    lesson._records.forEach((r: any) => {

                        const resourceId = r.ResourceId;
                        if (!resourceMap[resourceId]) {

                            resourceMap[resourceId] = { ...r, Records: [] };

                        }
                        resourceMap[resourceId].Records.push({ ...r });

                    });

                    // Aggregate and sort resources
                    const resources = Object.values(resourceMap)
                        .map((resource: any) => {

                            if (resource.Records.length > 1) {

                                return { ...resource, ...aggregateResourceMetrics(resource.Records) };

                            }
                            return resource;

                        })
                        .sort((a: any, b: any) => (a.ResourceDisplayOrder ?? 0) - (b.ResourceDisplayOrder ?? 0));

                    return { ...lesson, Resources: resources };

                })
                .sort((a, b) => (a.LessonDisplayOrder ?? 0) - (b.LessonDisplayOrder ?? 0));

            return { ...student, Lessons: lessons };

        });

        return mergedStudents;

    };

    useEffect(() => {

        if (studentView) {

            calculateAverages();

        }

    }, [tableRows, studentView]);

    useEffect(() => {

        if (tableRows.length > MAX_DISPLAY_ROWS) {

            createToast(
                formatMessage(
                    { id: 'showing_first_shown_records_of_total_ple.18a484b7', defaultMessage: 'Showing first {shown} records of {total}. Please refine your filters to narrow the results.' },
                    { shown: MAX_DISPLAY_ROWS, total: tableRows.length }
                ),
                'warning'
            );

        }

    }, [tableRows]);

    useEffect(() => {

        setState({ tableKey: GenerateUUID() });
        if (!studentView) {

            showResources();

        }

    }, [showResourcesSelected, studentView]);

    // only runs on student view and is displayed all the time
    const calculateAverages = useCallback(() => {

        const data = reportSections[0].Components[0].Results?.Data ?? [];
        const studentMap: Record<number, any[]> = {};

        data.forEach((item: any) => {

            if (!studentMap[item.StudentProfileId]) studentMap[item.StudentProfileId] = [];
            studentMap[item.StudentProfileId].push(item);

        });

        let totalProgress = 0;
        let totalTimeSpent = 0;
        let totalStarted = 0;
        let totalCompleted = 0;
        let totalActive = 0;

        const studentCount = Object.keys(studentMap).length;

        Object.values(studentMap).forEach(studentRecords => {

            const totalTimeForStudent = studentRecords.reduce((sum, r) => sum + (r.TimeSpentInMinutes || 0), 0);
            totalTimeSpent += totalTimeForStudent;

            const completed = studentRecords.some(r => r.CourseDateCompleted);
            if (completed) {

                totalProgress += 1;
                totalCompleted += 1;

            }

            // A student has "started" if any resource is beyond "Not Started". Attempts only
            // applies to assessments; other resource types are started/completed via getStatus.
            const started = studentRecords.some(r => getStatus(r) !== 'Not Started');
            if (started) totalStarted += 1;

            const active = studentRecords.some(r => r.CourseLastAccessDate && isDateWithinLast7Days(new Date(r.CourseLastAccessDate))
            );
            if (active) totalActive += 1;

        });

        setState({
            averageMetrics: {
                activePercent: (totalActive / studentCount) * 100,
                completedPercent: (totalCompleted / studentCount) * 100,
                startedPercent: (totalStarted / studentCount) * 100,
                studentProgress: (totalProgress / studentCount) * 100,
                timeSpent: totalTimeSpent / studentCount
            }
        });

    }, [reportSections]);

    const getMetricColumnConfig = () => {

        switch (currentMetric) {

            case 'Time Spent':
                return {
                    field: 'CourseSecondsSpent',
                    renderCell: (item: any) => calculateHourMinFormat(item.CourseSecondsSpent),
                    title: formatMessage({ id: 'total_time.ac5ce3ce', defaultMessage: 'Total Time' }),
                    visible: true
                };
            case 'Status':
                return { field: 'Attempts', renderCell: () => '', title: formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' }), visible: false };
            case 'Score':
            default:
                return { field: 'Attempts', renderCell: () => '', title: formatMessage({ id: 'average_score.3cb75673', defaultMessage: 'Average Score' }), visible: false };

        }

    };

    const transformData = (data: any, selectedResources = showResourcesSelected) => {

        const selectedValues = selectedResources.map((r: any) => r.value);
        const allKnownValues = ResourceTypeLabelsList.map(r => r.value);

        // --- Group by LessonName ---
        const lessonsMap: any = {};

        for (const item of data) {

            const lessonName: any = item.LessonName || '';
            if (!lessonsMap[lessonName]) {

                lessonsMap[lessonName] = {
                    LessonDisplayOrder: item.LessonDisplayOrder ?? 0,
                    LessonName: lessonName,
                    Records: []
                };

            }
            lessonsMap[lessonName].Records.push(item);

        }

        // --- Build resource-level structure ---
        const resourcesMap: any = {};

        Object.values(lessonsMap).forEach((lessonGroup: any) => {

            const lessonRecords = lessonGroup.Records;

            lessonRecords.forEach((rec: any) => {

                const resourceId = rec.ResourceId;
                if (!selectedValues.includes(rec.ResourceTypeID) && allKnownValues.includes(rec.ResourceTypeID)) return;

                if (!resourcesMap[resourceId]) {

                    resourcesMap[resourceId] = {
                        AllRecords: [],
                        LessonDisplayOrder: lessonGroup.LessonDisplayOrder,
                        LessonName: lessonGroup.LessonName,
                        ResourceDisplayOrder: rec.ResourceDisplayOrder,
                        ResourceId: rec.ResourceId,
                        ResourceName: rec.ResourceName,
                        ResourceTypeID: rec.ResourceTypeID,
                        StudentData: []
                    };

                }
                resourcesMap[resourceId].StudentData.push(rec);
                resourcesMap[resourceId].AllRecords.push(rec);

            });

        });

        // --- Build flat resource results ---
        const result = Object.values(resourcesMap)
            .map((res: any) => {

                const sd = res.StudentData;

                // Group student data by StudentProfileId for per-student metrics
                const studentMetricsMap: any = {};
                sd.forEach((rec: any) => {

                    if (!studentMetricsMap[rec.StudentProfileId]) {

                        studentMetricsMap[rec.StudentProfileId] = {
                            Records: [],
                            StudentName: rec.StudentName,
                            StudentProfileId: rec.StudentProfileId
                        };

                    }
                    studentMetricsMap[rec.StudentProfileId].Records.push(rec);

                });

                return {
                    ...res,
                    AverageAttempts: avg(sd.map((r: any) => r.Attempts)),
                    AverageScore: avg(sd.map((r: any) => r.HighestScore)),
                    AverageTimeSpent: avg(sd.map((r: any) => r.TimeSpentInMinutes)),
                    StudentData: Object.values(studentMetricsMap).map((studentMetric: any) => {

                        const records = studentMetric.Records;
                        const validScores = records.map((r: any) => r.HighestScore).filter((s: any) => s !== null && s !== undefined);
                        return {
                            Attempts: records.reduce((sum: number, r: any) => sum + (r.Attempts || 0), 0),
                            Completed: records.find((r: any) => r.Completed?.Answered)?.Completed ?? records[0]?.Completed ?? null,
                            CourseLastAccessDate: records[0]?.CourseLastAccessDate ?? null,
                            CourseStudentResourceId: records.find((r: any) => r.CourseStudentResourceId != null)?.CourseStudentResourceId ?? null,
                            ResourceTypeID: res.ResourceTypeID,
                            Score: validScores.length > 0 ? Math.max(...validScores) : null,
                            StudentName: studentMetric.StudentName,
                            StudentProfileId: studentMetric.StudentProfileId,
                            TimeSpent: records.reduce((sum: number, r: any) => sum + (r.TimeSpentInMinutes || 0), 0)
                        };

                    })
                };

            })
            .sort((a, b) => {

                if (a.LessonDisplayOrder !== b.LessonDisplayOrder) {

                    return a.LessonDisplayOrder - b.LessonDisplayOrder;

                }
                return a.ResourceDisplayOrder - b.ResourceDisplayOrder;

            });

        return result;

    };

    const getStudentMetricAggregate = (student: any) => {

        const allRecords: any[] = [];
        student.Lessons?.forEach((l: any) => l.Resources?.forEach((r: any) => allRecords.push(r)));
        switch (currentMetric) {

            case 'Time Spent':
                const minutes = allRecords.reduce((sum, r) => sum + (r.TimeSpentInMinutes || 0), 0);
                return calculateHourMinFormat(Math.round(minutes));
            case 'Attempts':
                const attempts = allRecords.reduce((sum, r) => sum + (r.Attempts || 0), 0);
                return attempts;
            case 'Status':

                // For aggregate status, show overall student status
                return statusLabel(getStatus(student));
            case 'Score':
            default:

                // For aggregate score, calculate average of assessment scores only
                const assessmentRecords = allRecords.filter(r => r.ResourceTypeID === 0);
                const scores = assessmentRecords.map(r => r.HighestScore).filter(s => s !== null && s !== undefined);
                const avgScore = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
                return scores.length ? normalizeScore(avgScore) : '---';

        }

    };

    const buildStudentExport = () => {

        const rows = sortedData.map((student: any) => {

            const columnName =
                currentMetric === 'Time Spent' ? formatMessage({ id: 'total_time.ac5ce3ce', defaultMessage: 'Total Time' }) :
                    currentMetric === 'Status' ? formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' }) :
                        currentMetric === 'Score' ? formatMessage({ id: 'average_score.3cb75673', defaultMessage: 'Average Score' }) :
                            formatMessage({ id: 'attempts.ae7c8eee', defaultMessage: 'Attempts' });
            const row: any = {
                [columnName]: getStudentMetricAggregate(student),
                [formatMessage({ id: 'completion.7cf80efc', defaultMessage: 'Completion' })]: formatDate(student.CourseDateCompleted),
                [formatMessage({ id: 'last_access.6fd7ff0b', defaultMessage: 'Last Access' })]: formatDate(student.CourseLastAccessDate),
                [formatMessage({ id: 'student_name.f4347b21', defaultMessage: 'Student Name' })]: student.StudentName
            };

            // Add resource columns that match visible filter
            const selectedValues = showResourcesSelected.map(s => s.value);

            // Build a unique set of lesson-resource headers for consistency
            const headerSet = new Map<string, number>();
            student.Lessons?.forEach((lesson: any) => {

                lesson.Resources?.forEach((res: any) => {

                    if (!selectedValues.includes(res.ResourceTypeID)) return;
                    const header = `${lesson.LessonName} - ${res.ResourceName}`;
                    headerSet.set(header, res.ResourceId);

                });

            });
            headerSet.forEach((resourceId, header) => {

                const val = getStudentResourceMetric(student, resourceId);
                if (currentMetric === 'Time Spent') {

                    row[header] = calculateHourMinFormat(Math.round(val || 0));

                } else if (currentMetric === 'Status') {

                    row[header] = val ? statusLabel(val) : '---';

                } else if (currentMetric === 'Score') {

                    row[header] = val !== null ? normalizeScore(val) : '---';

                } else {

                    row[header] = val ?? 0;

                }

            });
            return row;

        });
        return rows;

    };

    const buildResourceExport = () => {

        const rows = sortedData.map((res: any) => {

            const row: any = {
                [formatMessage({ id: 'average_score.3cb75673', defaultMessage: 'Average Score' })]: res.ResourceTypeID === 0 ? normalizeScore(Math.round(res.AverageScore ?? 0)) : '---',
                [formatMessage({ id: 'average_time.455bd085', defaultMessage: 'Average Time' })]: calculateHourMinFormat(Math.round(res.AverageTimeSpent ?? 0)),
                [formatMessage({ id: 'resource.6e70fb89', defaultMessage: 'Resource' })]: resourceTypeLabels.find(r => r.value === res.ResourceTypeID)?.name ?? EResourceType[res.ResourceTypeID]
            };

            // Add per-student metrics as columns
            students.forEach((student: any) => {

                const val = getResourceStudentMetric(res, student.StudentProfileId);
                const header = student.StudentName;
                if (currentMetric === 'Time Spent') {

                    row[header] = calculateHourMinFormat(Math.round(val || 0));

                } else if (currentMetric === 'Status') {

                    row[header] = val ? statusLabel(val) : '---';

                } else if (currentMetric === 'Score') {

                    row[header] = val !== null ? normalizeScore(val) : '---';

                } else {

                    row[header] = val ?? 0;

                }

            });
            return row;

        });
        return rows;

    };

    const handleExportCSV = useCallback(() => {

        const reportName = reportSections[0]?.Title || 'CourseSummaryReport';
        const fileName = buildExportFileName('Report', reportName, 'csv');
        setState({ exportFileName: fileName });
        const dataRows = studentView ? buildStudentExport() : buildResourceExport();
        setState({ exportRows: dataRows });
        setTimeout(() => {

            const csvLink = document.getElementById('csvLink');
            csvLink?.click();

        }, 0);

    }, [studentView, currentMetric, sortedData, showResourcesSelected, students]);

    const getStudentResourceTooltip = (studentData: any) => renderMetricsTooltip(studentData);

    // Initialize data on mount
    useEffect(() => {

        showStudents();
        setState({ students: getUniqueStudentNames(reportSections[0].Components[0].Results?.Data) });

    }, []);

    useEffect(() => {

        setState({ tableKey: GenerateUUID() });

    }, [studentView]);

    return (
        <>
            <StudentMetricsDisplay
                averageMetrics={averageMetrics}
                calculateHourMinFormat={calculateHourMinFormat}
            />
            <Card>
                <CSVLink
                    className="d-none"
                    data={exportRows}
                    filename={exportFileName}
                    id="csvLink"
                    type="button"
                >
                </CSVLink>
                <div className="p-3 d-flex justify-content-between">
                    <ViewOptionsBar buttonGroups={[categories, metrics]}>
                        <div className="ms-4">
                            <GMetrixDropdown
                                multiSelect
                                dataItemKey="value"
                                dataItems={resourceTypeLabels}
                                filterable={false}
                                label={formatMessage({ id: 'show_course_resources.a7ff7100', defaultMessage: 'Show Course Resources' })}
                                onChange={e => setState({ showResourcesSelected: e as any })}
                                style={{ maxWidth: '250px' }}
                                textField="name"
                                value={showResourcesSelected}
                            />
                        </div>
                    </ViewOptionsBar>
                    <div className="mt-4">
                        <GMetrixButton onClick={handleExportCSV} variant="primary">
                            <FormattedMessage defaultMessage="Export to CSV" id="export_to_csv.e1de1359" />
                        </GMetrixButton>
                    </div>
                </div>
                {studentView ? (
                    <div style={{ maxHeight: 'calc(100vh - 70px - 24px - 105px)', overflowX: 'auto', overflowY: 'auto' }}>
                        <Table bordered hover responsive className="kendo-table mb-0">
                            <thead>
                                {/* Row 1: group headers */}
                                <tr>
                                    <th rowSpan={2} style={{ backgroundColor: 'white', left: 0, minWidth: 150, position: 'sticky', zIndex: 2 }}>
                                    </th>
                                    <th className="tableHeader pt-0" colSpan={(getMetricColumnConfig().visible ? 1 : 0) + 2}>
                                        <FormattedMessage defaultMessage="Statistics" id="statistics.0d26e35d" />
                                    </th>
                                    {studentViewLessonColumns.map((lesson: any) => (
                                        <th className="tableHeader" colSpan={lesson.resources.length} key={lesson.lessonName}>
                                            <ReportToolTip copyText={lesson.lessonName} id={`${lesson.lessonName}-current-metric`} tooltip={lesson.lessonName} trigger="hover">
                                                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {lesson.lessonName}
                                                </span>
                                            </ReportToolTip>
                                        </th>
                                    ))}
                                </tr>
                                {/* Row 2: individual column headers */}
                                <tr>
                                    {getMetricColumnConfig().visible && (
                                        <th
                                            className="text-center"
                                            onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: getMetricColumnConfig().field }] })}
                                            style={{ cursor: 'pointer', minWidth: 150 }}
                                        >
                                            {sortableHeader(
                                                <div className="d-flex justify-content-center">
                                                    {getMetricColumnConfig().title}
                                                </div>,
                                                getMetricColumnConfig().field,
                                                sort
                                            )}
                                        </th>
                                    )}
                                    <th
                                        className="text-center"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'CourseLastAccessDate' }] })}
                                        style={{ cursor: 'pointer', minWidth: 150 }}
                                    >
                                        {sortableHeader(
                                            <span className="d-flex justify-content-center" title={formatMessage({ id: 'last_access.6fd7ff0b', defaultMessage: 'Last Access' })}>
                                                <FormattedMessage defaultMessage="Last Access" id="last_access.6fd7ff0b" />
                                            </span>,
                                            'CourseLastAccessDate',
                                            sort
                                        )}
                                    </th>
                                    <th
                                        className="text-center"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'CourseDateCompleted' }] })}
                                        style={{ cursor: 'pointer', minWidth: 150 }}
                                    >
                                        {sortableHeader(
                                            <span className="d-flex justify-content-center" title={formatMessage({ id: 'completion.7cf80efc', defaultMessage: 'Completion' })}>
                                                <FormattedMessage defaultMessage="Completion" id="completion.7cf80efc" />
                                            </span>,
                                            'CourseDateCompleted',
                                            sort
                                        )}
                                    </th>
                                    {studentViewLessonColumns.flatMap((lesson: any) => lesson.resources.map((resource: any) => (
                                        <th
                                            className="text-center"
                                            key={resource.ResourceId}
                                            onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: `resource-${resource.ResourceId}` }] })}
                                            style={{ cursor: 'pointer', minWidth: EXPANDED_COLUMN_WIDTH }}
                                        >
                                            {sortableHeader(
                                                <div className="text-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    <ReportToolTip copyText={resource.ResourceName} id={`${resource.ResourceName}-current-metric`} tooltip={resource.ResourceName} trigger="hover">
                                                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {resource.ResourceName}
                                                        </span>
                                                    </ReportToolTip>
                                                </div>,
                                                `resource-${resource.ResourceId}`,
                                                sort
                                            )}
                                        </th>
                                    ))
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {displayData.map((student: any, rowIndex: number) => (
                                    <tr key={`${student.StudentProfileId}-${rowIndex}`}>
                                        <td style={{ backgroundColor: 'white', left: 0, overflowX: 'hidden', position: 'sticky', zIndex: 1 }}>
                                            <ReportToolTip copyText={student.StudentName} id={student.StudentName + '_tooltip'} tooltip={student.StudentName} trigger="hover">
                                                <div
                                                    onClick={() => navigateToStudentDetail(student.StudentProfileId)}
                                                    style={{ color: '#0066cc', cursor: 'pointer', overflow: 'hidden', textDecoration: 'underline', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                >
                                                    {student.StudentName}
                                                </div>
                                            </ReportToolTip>
                                        </td>
                                        {getMetricColumnConfig().visible && (
                                            <td className="text-center">
                                                {getMetricColumnConfig().renderCell(student)}
                                            </td>
                                        )}
                                        <td className="text-center">
                                            {formatDisplayDate(student.CourseLastAccessDate)}
                                        </td>
                                        <td className="text-center">
                                            {formatDisplayDate(student.CourseDateCompleted)}
                                        </td>
                                        {studentViewLessonColumns.flatMap((lesson: any) => lesson.resources.map((resource: any) => {

                                            const studentLesson = student.Lessons?.find(
                                                (l: any) => l.LessonName === lesson.lessonName
                                            );
                                            const studentResource = studentLesson?.Resources?.find(
                                                (r: any) => r.ResourceId === resource.ResourceId
                                            );

                                            if (!studentResource) return (
                                                <td className="text-center" key={resource.ResourceId}>
                                                    ---
                                                </td>
                                            );

                                            const metricDisplay = getMetricValue(studentResource, currentMetric);
                                            const copyText =
                                                currentMetric === 'Time Spent' ? studentResource.TimeSpentInMinutes :
                                                    currentMetric === 'Attempts' ? studentResource.Attempts :
                                                        currentMetric === 'Status' ? statusLabel(getStatus(studentResource)) :
                                                            currentMetric === 'Score' && studentResource.ResourceTypeID === 0 ? studentResource.HighestScore :
                                                                null;

                                            return (
                                                <td className="text-center" key={resource.ResourceId}>
                                                    <ReportToolTip
                                                        copyText={copyText}
                                                        id={currentMetric === 'Score' ? `score-tooltip-${resource.ResourceId}-${student.StudentProfileId}` : undefined}
                                                        tooltip={renderMetricsTooltip(studentResource)}
                                                    >
                                                        {metricDisplay}
                                                    </ReportToolTip>
                                                </td>
                                            );

                                        })
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                ) : (
                    <div style={{ maxHeight: 'calc(100vh - 70px - 24px - 105px)', overflowX: 'auto', overflowY: 'auto' }}>
                        <Table bordered hover responsive className="kendo-table mb-0">
                            <thead>
                                <tr>
                                    <th
                                        className="text-center tableHeader"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'ResourceTypeID' }] })}
                                        style={{ cursor: 'pointer', left: 0, minWidth: 200, position: 'sticky', zIndex: 1 }}
                                    >
                                        {sortableHeader(
                                            <span className="d-flex justify-content-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={formatMessage({ id: 'resource.6e70fb89', defaultMessage: 'Resource' })}>
                                                <FormattedMessage defaultMessage="Resource" id="resource.6e70fb89" />
                                            </span>,
                                            'ResourceTypeID',
                                            sort
                                        )}
                                    </th>
                                    <th
                                        className="text-center tableHeader"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'AverageTimeSpent' }] })}
                                        style={{ cursor: 'pointer', minWidth: 150 }}
                                    >
                                        {sortableHeader(
                                            <span className="d-flex justify-content-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={formatMessage({ id: 'average_time.455bd085', defaultMessage: 'Average Time' })}>
                                                <FormattedMessage defaultMessage="Average Time" id="average_time.455bd085" />
                                            </span>,
                                            'AverageTimeSpent',
                                            sort
                                        )}
                                    </th>
                                    <th
                                        className="text-center tableHeader"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'AverageScore' }] })}
                                        style={{ cursor: 'pointer', minWidth: 150 }}
                                    >
                                        {sortableHeader(
                                            <span className="d-flex justify-content-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={formatMessage({ id: 'average_score.3cb75673', defaultMessage: 'Average Score' })}>
                                                <FormattedMessage defaultMessage="Average Score" id="average_score.3cb75673" />
                                            </span>,
                                            'AverageScore',
                                            sort
                                        )}
                                    </th>
                                    {students.map((student: any) => (
                                        <th
                                            className="text-center"
                                            key={student.StudentProfileId}
                                            onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: `student-${student.StudentProfileId}` }] })}
                                            style={{ cursor: 'pointer', minWidth: EXPANDED_COLUMN_WIDTH }}
                                        >
                                            {sortableHeader(
                                                <span className="d-flex justify-content-center text-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    <ReportToolTip id={student.StudentName + '_tooltip'} tooltip={student.StudentName} trigger="hover">
                                                        <span
                                                            onClick={() => navigateToStudentDetail(student.StudentProfileId)}
                                                            style={{ color: '#0066cc', cursor: 'pointer', display: 'block', overflow: 'hidden', textDecoration: 'underline', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                        >
                                                            {student.StudentName}
                                                        </span>
                                                    </ReportToolTip>
                                                </span>,
                                                `student-${student.StudentProfileId}`,
                                                sort
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {displayData.map((item: any, rowIndex: number) => (
                                    <tr key={`${item.ResourceId}-${rowIndex}`}>
                                        <td className="text-center" style={{ backgroundColor: 'white', left: 0, overflowX: 'hidden', position: 'sticky', zIndex: 1 }}>
                                            <ReportToolTip tooltip={item.ResourceName}>
                                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {item.ResourceName}
                                                </div>
                                            </ReportToolTip>
                                        </td>
                                        <td className="text-center">
                                            {calculateHourMinFormat(Math.round(item.AverageTimeSpent))}
                                        </td>
                                        <td className="text-center">
                                            {item.ResourceTypeID === 0 ? renderScore(Math.round(item.AverageScore), true) : '---'}
                                        </td>
                                        {students.map((student: any) => {

                                            const studentData = item.StudentData.find(
                                                (s: any) => s.StudentProfileId === student.StudentProfileId
                                            );
                                            if (!studentData) return (
                                                <td className="text-center" key={student.StudentProfileId}>
                                                    ---
                                                </td>
                                            );

                                            let display: string | number | React.ReactElement = '---';
                                            const isAssessment = item.ResourceTypeID === 0;
                                            switch (currentMetric) {

                                                case 'Time Spent':
                                                    display = studentData.TimeSpent > 0
                                                        ? calculateHourMinFormat(Math.round(studentData.TimeSpent))
                                                        : '---';
                                                    break;
                                                case 'Attempts':
                                                    display = studentData.Attempts > 0
                                                        ? `${Math.round(studentData.Attempts)}x`
                                                        : '---';
                                                    break;
                                                case 'Status':
                                                    display = renderStatus(getStatus(studentData));
                                                    break;
                                                case 'Score':
                                                    if (isAssessment) {

                                                        display = (studentData.Attempts === 0 || !studentData.Attempts)
                                                            ? <FormattedMessage defaultMessage="No Score" id="no_score.defd94f7" />
                                                            : renderScore(Math.round(studentData.Score));

                                                    } else {

                                                        display = '---';

                                                    }
                                                    break;
                                                default:
                                                    display = '---';
                                                    break;

                                            }

                                            return (
                                                <td className="text-center" key={student.StudentProfileId}>
                                                    <ReportToolTip tooltip={getStudentResourceTooltip(studentData)}>
                                                        {display}
                                                    </ReportToolTip>
                                                </td>
                                            );

                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

            </Card>
        </>
    );

};